// src/features/MedicalServices/page/Category/CategoryNewForm.tsx

import React, { useEffect } from 'react';
import { Form, Input, Button, Breadcrumb, message, Spin, Card } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { post, get } from '@/common/services/httpClient';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useUnsavedChangesStore } from '@/app/store/unsavedChangesStore';
import { useNavigationBlocker } from '@/common/hooks/useNavigationBlocker';
import { useUnsavedChangesWarning } from '@/common/hooks/useUnsavedChangesWarning';
import { API_ENDPOINTS } from '../../api/apiEndpoints';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';

interface CategorySchema {
    id?: number;
    name: string;
    description: string;
}

interface ApiResponse {
    isError: boolean;
    message: string;
}

// --- API calls ---
const saveCategory = (data: CategorySchema): Promise<ApiResponse> =>
    post(API_ENDPOINTS.MEDICALSERVICES.SAVE_SERVICECATEGORY, data);

const updateCategory = (data: CategorySchema): Promise<ApiResponse> =>
    post(API_ENDPOINTS.MEDICALSERVICES.UPDATE_SERVICECATEGORY, data);

const getCategoryById = async (id: string): Promise<CategorySchema> => {
    const res = await get(`${API_ENDPOINTS.MEDICALSERVICES.GET_SERVICECATEGORY_BY_ID}/${id}`);
    return res.data;
};

// --- Company Service Types ---
interface ServiceItem {
    id?: number;
    name?: string;
    description?: string;
}

interface ServiceCategory {
    id?: number;
    name: string;
    items: ServiceItem[];
}

// --- Company Service Grid Component ---
// --- Company Service Grid Component ---
const CompanyService = ({ form }: { form: any }) => {
    const { t } = useTranslation();

    return (
        <Card title={t('Add Sub Service')} style={{ marginTop: 24 }} bodyStyle={{ padding: 16 }}>
            <Form.List name="companyServices">
                {(fields, { add, remove }) => (
                    <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e5e7eb', overflow: 'hidden' }}>

                        {/* Table Header */}
                        <div style={{ display: 'grid', gridTemplateColumns: '32px 1fr 2fr auto', gap: 12, padding: '10px 12px', background: '#f3f4f6', borderBottom: '2px solid #e5e7eb', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', color: 'rgba(0,0,0,0.88)' }}>
                            <div>#</div>
                            <div>{t('Name')}</div>
                            <div>{t('Description')}</div>
                            <div></div>
                        </div>

                        {/* Service Rows */}
                        {fields.map((field, index) => (
                            <div key={field.key} style={{ display: 'grid', gridTemplateColumns: '32px 1fr 2fr auto', gap: 12, alignItems: 'center', padding: '10px 12px', background: index % 2 === 0 ? '#fafbfd' : '#ffffff', borderBottom: '1px solid #f0f2f5' }}>
                                <div style={{ width: 24, height: 24, borderRadius: 6, background: '#0f85ae', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600 }}>
                                    {index + 1}
                                </div>

                                <Form.Item
                                    {...field}
                                    name={[field.name, 'name']}
                                    fieldKey={[field.fieldKey, 'name']}
                                    rules={[{ required: true, message: t('Please enter name') }]}
                                    style={{ marginBottom: 0 }}
                                >
                                    <Input placeholder={t('Enter name')} />
                                </Form.Item>

                                <Form.Item
                                    {...field}
                                    name={[field.name, 'description']}
                                    fieldKey={[field.fieldKey, 'description']}
                                    rules={[{ required: true, message: t('Please enter description') }]}
                                    style={{ marginBottom: 0 }}
                                >
                                    <Input placeholder={t('Enter description')} />
                                </Form.Item>

                                {/* Disable delete button if only one row */}
                                <Button
                                    danger
                                    type="text"
                                    icon={<DeleteOutlined />}
                                    onClick={() => {
                                        if (fields.length === 1) {
                                            message.warning(t('At least one sub-service is required'));
                                            return;
                                        }
                                        remove(field.name);
                                    }}
                                />
                            </div>
                        ))}

                        {/* Add Button */}
                        <div style={{ padding: '12px', background: '#f9fafb', borderTop: '1px solid #e5e7eb' }}>
                            <Button
                                type="dashed"
                                block
                                icon={<PlusOutlined />}
                                style={{ height: 36, borderColor: '#d1d5db', color: '#0891B2', fontWeight: 500 }}
                                onClick={() => {
                                    const currentValues = form.getFieldValue('companyServices') || [];
                                    const lastItem = currentValues[currentValues.length - 1];

                                    // Prevent adding a new row if last row is empty
                                    if (!lastItem?.name?.trim() || !lastItem?.description?.trim()) {
                                        message.warning(t('Please fill the existing sub-service before adding a new one'));
                                        return;
                                    }

                                    add({ name: '', description: '' });
                                }}
                            >
                                {t('Add Sub Service')}
                            </Button>
                        </div>
                    </div>
                )}
            </Form.List>
        </Card>
    );
};

// --- Main Component ---
const CategoryNewForm = () => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const { isDirty, setIsDirty } = useUnsavedChangesStore();

    useNavigationBlocker(isDirty);
    useUnsavedChangesWarning();

    const { state } = useRouter().state.location;
    const record = (state as { record?: Record<string, any> })?.record;
    const id = record?.id?.toString();
    const isEdit = !!id;

    const { data, isLoading } = useQuery({
        queryKey: ['category', id],
        queryFn: () => getCategoryById(id!),
        enabled: !!id,
    });

    useEffect(() => {
        if (data) {
            const mainService = data.item1?.[0] || {};

            const companyServices = data.item2?.length
                ? data.item2.map((item: any) => ({
                    id: item.id,
                    name: item.name,
                    description: item.description,
                    isActive: item.isActive,
                }))
                : [{ name: '', description: '' }];

            form.setFieldsValue({
                name: mainService.name || '',
                description: mainService.description || '',
                companyServices,
            });
        } else {
            form.setFieldsValue({
                companyServices: [{ name: '', description: '' }],
            });
        }
    }, [data, form]);


    const addMutation = useMutation({
        mutationFn: saveCategory,
        onSuccess: (res) => {
            if (res.isError) return message.error(res.message);
            message.success(t('Category created successfully'));
            queryClient.invalidateQueries({ queryKey: ['categoryList'] });
            setIsDirty(false);
            navigate({ to: '..', skipBlock: true } as never);
        },
    });

    const updateMutation = useMutation({
        mutationFn: updateCategory,
        onSuccess: (res) => {
            if (res.isError) return message.error(res.message);
            message.success(t('Category updated successfully'));
            queryClient.invalidateQueries({ queryKey: ['categoryList'] });
            setIsDirty(false);
            navigate({ to: '..', skipBlock: true } as never);
        },
    });

    const onFinish = (values: any) => {
        const payload = {
            name: values.name,
            description: values.description,
            isActive: true,
            serviceCategoryItemRequests: values.companyServices.map((item: any) => ({
                name: item.name,
                description: item.description,
                isActive: true
            }))
        };

        isEdit
            ? updateMutation.mutate({ ...payload, id: Number(id) })
            : addMutation.mutate(payload);
    };

    if (isLoading) return <div className="centered"><Spin size="large" /></div>;

    return (
        <div className="page-content">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <Breadcrumb>
                    <Breadcrumb.Item>
                        <a onClick={() => { setIsDirty(false); navigate({ to: '/medical-services/categories' }); }}>
                            {t('Medical Services')}
                        </a>
                    </Breadcrumb.Item>
                    <Breadcrumb.Item>{t('Main Service')}</Breadcrumb.Item>
                    <Breadcrumb.Item>{t('Form')}</Breadcrumb.Item>
                </Breadcrumb>
                <Button onClick={() => { setIsDirty(false); navigate({ to: '..' }); }}>{t('common.goBack')}</Button>
            </div>

            <Card title={t('Main Service')}>
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                    onValuesChange={() => setIsDirty(true)}
                >
                    <Form.Item
                        label={t('Name')}
                        name="name"
                        rules={[{ required: true, message: t('Please enter name') }]}
                    >
                        <Input placeholder={t('Enter name')} />
                    </Form.Item>

                    <Form.Item
                        label={t('Description')}
                        name="description"
                        rules={[{ required: true, message: t('Please enter description') }]}
                    >
                        <Input placeholder={t('Enter description')} />
                    </Form.Item>

                    {/* Company Service Grid */}
                    <CompanyService form={form} />

                    <Form.Item style={{ textAlign: 'right', marginTop: 24 }}>
                        <Button type="primary" htmlType="submit" loading={addMutation.isPending || updateMutation.isPending}>
                            {isEdit ? t('Update') : t('Save')}
                        </Button>
                        <Button htmlType="button" style={{ marginLeft: 8 }} onClick={() => form.resetFields()}>
                            {t('Reset')}
                        </Button>
                    </Form.Item>
                </Form>
            </Card>
        </div>
    );
};

export default CategoryNewForm;